/* @jsx h */
import { computed, defineComponent, h, ref } from "vue";
import { LemmonModal, useLocalization } from "@lemmonhq/uikit";
import { useToast, useTrpc } from "@app/vue/providers/index";
import type { Role } from "../../Team/types";
import { blockMinutes, type Block, type DayTemplate } from "../types";

type Props = { template: DayTemplate | null; roles: Role[]; onClose: (changed: boolean) => void };

const newBlock = (start = "11:00", end = "16:00"): Block => ({ name: null, start_time: start, end_time: end, break_minutes: 0, slots: [] });

const hours = (mins: number) => `${Math.floor(mins / 60)}h${mins % 60 ? String(mins % 60).padStart(2, "0") : ""}`;

// A typical day: tabs per time block, the block's hours large, then the
// roles it needs and how many of each.
export const TemplateEditor = defineComponent<Props>({
  name: "TemplateEditor",
  props: ["template", "roles", "onClose"],
  setup(props) {
    const { t } = useLocalization();
    const trpc = useTrpc();
    const toast = useToast();
    const tpl = props.template;

    const name = ref(tpl?.name ?? "");
    const code = ref(tpl?.code ?? "");
    const blocks = ref<Block[]>(tpl?.blocks.length ? tpl.blocks.map((b) => ({ ...b, slots: [...b.slots] })) : [newBlock(), newBlock("17:00", "23:00")]);
    const active = ref(0);
    const saving = ref(false);

    const block = computed(() => blocks.value[active.value]);
    const setBlock = (patch: Partial<Block>) => (blocks.value = blocks.value.map((b, i) => (i === active.value ? { ...b, ...patch } : b)));
    const countOf = (roleId: number) => block.value?.slots.find((s) => s.role_id === roleId)?.headcount ?? 0;
    const setCount = (roleId: number, n: number) => {
      const others = block.value.slots.filter((s) => s.role_id !== roleId);
      setBlock({ slots: n > 0 ? [...others, { role_id: roleId, headcount: n }] : others });
    };
    const totalSlots = computed(() => blocks.value.reduce((s, b) => s + b.slots.reduce((x, sl) => x + sl.headcount, 0), 0));

    const save = async () => {
      saving.value = true;
      try {
        await trpc.rota.templates.save.mutate({
          id: tpl?.id,
          name: name.value.trim(),
          code: code.value.trim() || null,
          blocks: blocks.value.map((b) => ({
            name: b.name?.trim() || null,
            start_time: b.start_time,
            end_time: b.end_time,
            break_minutes: b.break_minutes || 0,
            slots: b.slots,
          })),
        });
        toast.showToast(t("workday.common.saved"), { type: "success" });
        props.onClose(true);
      } catch (e) {
        toast.showToast((e as { message?: string })?.message ?? t("workday.common.save_failed"), { type: "error" });
      } finally {
        saving.value = false;
      }
    };

    const remove = async () => {
      if (!tpl || !confirm(t("workday.rota.template_delete_confirm").replace("{name}", tpl.name))) return;
      await trpc.rota.templates.delete.mutate({ id: tpl.id });
      props.onClose(true);
    };

    const renderBlock = () => {
      const b = block.value;
      if (!b) return null;
      return (
        <div class="space-y-4 rounded-2xl border border-base-300 bg-base-200 p-5">
          <div class="flex items-center justify-center gap-3">
            <input type="time" class="input input-bordered h-14 w-48 text-center text-2xl font-semibold tabular-nums" value={b.start_time} onChange={(e: Event) => setBlock({ start_time: (e.target as HTMLInputElement).value })} />
            <span class="text-3xl font-semibold">–</span>
            <input type="time" class="input input-bordered h-14 w-48 text-center text-2xl font-semibold tabular-nums" value={b.end_time} onChange={(e: Event) => setBlock({ end_time: (e.target as HTMLInputElement).value })} />
          </div>
          <div class="flex flex-wrap items-center justify-center gap-3 text-xs text-base-content/60">
            <span>{hours(blockMinutes(b))}</span>
            {b.end_time <= b.start_time && <span class="badge badge-sm">{t("workday.rota.overnight")}</span>}
            <label class="flex items-center gap-1">
              {t("workday.rota.break")}
              <input
                type="number"
                min="0"
                step="5"
                class="input input-xs w-14 input-bordered text-right"
                value={b.break_minutes || ""}
                onChange={(e: Event) => setBlock({ break_minutes: Number((e.target as HTMLInputElement).value) || 0 })}
              />
              min
            </label>
            <input
              class="input input-xs w-32 input-bordered"
              placeholder={t("workday.rota.block_name_placeholder")}
              value={b.name ?? ""}
              onInput={(e: Event) => setBlock({ name: (e.target as HTMLInputElement).value })}
            />
          </div>
          <div>
            <div class="mb-2 text-xs uppercase tracking-wide text-base-content/60">{t("workday.rota.staffing_roles")}</div>
            {props.roles.length === 0 ? (
              <p class="text-sm text-base-content/60">{t("workday.team.no_roles")}</p>
            ) : (
              <ul class="divide-y divide-base-300">
                {props.roles.map((r) => {
                  const n = countOf(r.id);
                  return (
                    <li key={r.id} class="flex items-center justify-between py-2">
                      <span class={n ? "font-medium" : "text-base-content/60"}>{r.name}</span>
                      <span class="flex items-center gap-2">
                        <button class="btn btn-circle btn-outline btn-xs border-base-300" disabled={!n} aria-label="−" onClick={() => setCount(r.id, n - 1)}>
                          <span class="material-symbols-outlined text-base">remove</span>
                        </button>
                        <span class="w-8 text-center font-semibold tabular-nums">{n ? `× ${n}` : "—"}</span>
                        <button class="btn btn-circle btn-outline btn-xs border-base-300" aria-label="+" onClick={() => setCount(r.id, n + 1)}>
                          <span class="material-symbols-outlined text-base">add</span>
                        </button>
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          {blocks.value.length > 1 && (
            <div class="text-right">
              <button
                class="btn btn-ghost btn-xs text-base-content/60"
                onClick={() => {
                  blocks.value = blocks.value.filter((_, i) => i !== active.value);
                  active.value = Math.max(0, active.value - 1);
                }}
              >
                <span class="material-symbols-outlined text-sm">delete</span>
                {t("workday.rota.remove_block")}
              </button>
            </div>
          )}
        </div>
      );
    };

    return () => (
      <LemmonModal
        title={tpl ? t("workday.rota.template_edit_title") : t("workday.rota.template_new_title")}
        onClose={() => props.onClose(false)}
        renderButtons={() => (
          <div class="border-t p-4">
            <div class="flex justify-between gap-2">
              <div class="flex gap-2">
                <button class="btn btn-neutral" onClick={() => props.onClose(false)}>
                  {t("general.words.cancel")}
                </button>
                {tpl && (
                  <button class="btn btn-ghost text-error" onClick={remove}>
                    {t("workday.common.delete")}
                  </button>
                )}
              </div>
              <button class="btn btn-primary" disabled={saving.value || !name.value.trim() || !blocks.value.length} onClick={save}>
                {t("workday.common.save")}
              </button>
            </div>
          </div>
        )}
      >
        <div class="space-y-4 p-6">
          <div class="grid grid-cols-[1fr_6rem] gap-3">
            <label class="block">
              <span class="mb-1 block text-sm font-medium">{t("workday.common.name")}</span>
              <input class="input input-bordered w-full" v-model={name.value} placeholder={t("workday.rota.template_name_placeholder")} />
            </label>
            <label class="block">
              <span class="mb-1 block text-sm font-medium">{t("workday.rota.code")}</span>
              <input class="input input-bordered w-full uppercase" maxlength={3} v-model={code.value} placeholder="FR" />
            </label>
          </div>
          <div class="flex items-center justify-between">
            <span class="text-sm font-medium">{t("workday.rota.blocks")}</span>
            <span class="badge badge-ghost">{t("workday.rota.shift_count").replace("{count}", String(totalSlots.value))}</span>
          </div>
          <div class="flex flex-wrap gap-1 rounded-xl bg-base-200 p-1">
            {blocks.value.map((b, i) => (
              <button key={i} class={["btn btn-sm flex-1", active.value === i ? "bg-base-100 shadow-sm" : "btn-ghost"].join(" ")} onClick={() => (active.value = i)}>
                {b.name || t("workday.rota.block_n").replace("{n}", String(i + 1))}
                <span class="text-[11px] font-normal text-base-content/50">{`${b.start_time}–${b.end_time}`}</span>
              </button>
            ))}
            <button
              class="btn btn-ghost btn-sm btn-square"
              aria-label={t("workday.rota.add_block")}
              onClick={() => {
                const last = blocks.value[blocks.value.length - 1];
                blocks.value = [...blocks.value, newBlock(last?.end_time ?? "11:00", last ? "23:00" : "16:00")];
                active.value = blocks.value.length - 1;
              }}
            >
              <span class="material-symbols-outlined">add</span>
            </button>
          </div>
          {renderBlock()}
        </div>
      </LemmonModal>
    );
  },
});
